import { FocusEvent, ForwardedRef, PropsWithChildren, useCallback, useId, useMemo, useRef, useState } from 'react';
import {
  chakraComponents,
  ChakraStylesConfig,
  CreatableProps,
  CreatableSelect,
  GroupBase,
  InputActionMeta,
  OnChangeValue,
  Props as ReactSelectProps,
  Select as ReactSelect,
  SelectInstance,
} from 'chakra-react-select';
import type { OptionBase, SelectComponentsConfig, SizeProp, Variant } from 'chakra-react-select';
import { BoxProps, ThemingProps, useStyleConfig } from '@chakra-ui/react';
import { forwardRefGeneric } from '@netiva/classifieds-common';

import { SelectComponentName } from '../theme';

export type SelectOption<TValue = string> = OptionBase & {
  label: string;
  value: TValue;
};

export type RenderOption<TOption> = ({ children, option }: PropsWithChildren<{ option: TOption }>) => React.ReactNode;

type MultiValue<T> = T[];
type SingleValue<T> = T | undefined;

export type SelectValue<TValue, IsMulti extends boolean> = IsMulti extends true
  ? MultiValue<TValue>
  : SingleValue<TValue>;

export type SelectProps<
  TValue,
  TOption extends SelectOption<TValue> = SelectOption<TValue>,
  IsMulti extends boolean = boolean,
> = ThemingProps & {
  'aria-label'?: string;
  name?: string;
  placeholder?: string;
  options?: TOption[];
  isLoading?: boolean;
  isCreatable?: boolean;
  isClearable?: boolean;
  isMulti?: IsMulti;
  autoFocus?: boolean;
  onChange?: (value: SelectValue<TValue, IsMulti>) => void;
  value?: TValue | TValue[];
  onInputChange?: (value: string) => void;
  onCreate?: (value: string) => void;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  ignoreValueCase?: boolean;
  containerProps?: BoxProps;
  renderOption?: RenderOption<TOption>;
};

function SelectInner<
  TValue,
  TOption extends SelectOption<TValue> = SelectOption<TValue>,
  IsMulti extends boolean = false,
>(
  {
    isCreatable,
    colorScheme,
    onCreate,
    onChange,
    options,
    value,
    isMulti,
    placeholder,
    ignoreValueCase,
    containerProps,
    variant,
    size,
    renderOption,
    onFocus,
    onBlur,
    onInputChange,
    ...rest
  }: SelectProps<TValue, TOption, IsMulti>,
  ref: ForwardedRef<SelectInstance<TOption, IsMulti>>
) {
  const id = useId();

  // we use the chakra theming context for defining the custom react-select styles, so we need to cast them back to the actual type
  const styles = useStyleConfig(SelectComponentName, { colorScheme, ...rest }) as ChakraStylesConfig<TOption, IsMulti>;

  const handleChange = useCallback(
    (value: OnChangeValue<TOption, IsMulti>) => {
      if (!onChange) {
        return;
      }
      if (!value) {
        // @ts-expect-error TODO
        onChange(isMulti ? [] : undefined);
      } else if (isMulti) {
        // @ts-expect-error TODO
        onChange(value.map((v) => v.value));
      } else {
        // @ts-expect-error TODO
        onChange(value.value);
      }
    },
    [isMulti, onChange]
  );

  // browsers can autofill an address-shaped form's fields directly, bypassing focus/keystrokes
  // entirely; a real selection can only ever change through user interaction while focused, so any
  // input-change reported while unfocused is autofill noise, unless it exactly matches a known
  // option - then it's a real (if unusual) way of picking that option and gets applied as such.
  const isFocusedRef = useRef(false);
  const [inputValue, setInputValue] = useState('');

  const findExactOption = useCallback(
    (text: string) => {
      const normalized = text.trim().toLowerCase();
      if (!options || !normalized) {
        return undefined;
      }
      return options.find(
        (o) => o.label.trim().toLowerCase() === normalized || String(o.value).trim().toLowerCase() === normalized
      );
    },
    [options]
  );

  const handleFocus = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      isFocusedRef.current = true;
      onFocus?.(event);
    },
    [onFocus]
  );

  const handleBlur = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      isFocusedRef.current = false;
      setInputValue('');
      onBlur?.(event);
    },
    [onBlur]
  );

  const handleInputChange = useCallback(
    (newValue: string, actionMeta: InputActionMeta) => {
      if (actionMeta.action === 'input-change' && !isFocusedRef.current) {
        const matched = !isMulti && findExactOption(newValue);
        if (matched) {
          // @ts-expect-error TODO
          handleChange(matched);
        }
        setInputValue('');
        return;
      }
      setInputValue(newValue);
      onInputChange?.(newValue);
    },
    [findExactOption, handleChange, isMulti, onInputChange]
  );

  const isPrimitive = (value: unknown) => typeof value === 'string' || typeof value === 'number';

  const selectedOptions = useMemo(() => {
    if (!options) {
      return undefined;
    }

    const equals = (a?: TValue, b?: TValue) => {
      // compare strings and numbers
      if (isPrimitive(a) && isPrimitive(b)) {
        const aStr = String(a);
        const bStr = String(b);
        return ignoreValueCase ? aStr.toLowerCase() === bStr.toLowerCase() : aStr === bStr;
      }
      return a === b;
    };

    if (isMulti) {
      if (Array.isArray(value)) {
        return options.filter((o) => value.some((v) => equals(v, o.value)));
      }
      return options.filter((o) => equals(o.value, value));
    }

    return options.find((o) => equals(o.value, value as TValue));
  }, [ignoreValueCase, isMulti, options, value]);

  const components = useMemo<SelectComponentsConfig<TOption, IsMulti, GroupBase<TOption>> | undefined>(
    () =>
      renderOption
        ? {
            Option: ({ children, ...props }) => (
              <chakraComponents.Option {...props}>
                {renderOption({ children, option: props.data })}
              </chakraComponents.Option>
            ),
          }
        : undefined,
    [renderOption]
  );

  const commonProps: CreatableProps<TOption, IsMulti, GroupBase<TOption>> | ReactSelectProps<TOption, IsMulti> = {
    instanceId: id,
    chakraStyles: { ...styles, container: (provided) => ({ ...provided, ...containerProps }) },
    focusBorderColor: `${colorScheme}.500`,
    selectedOptionColorScheme: colorScheme,
    placeholder: placeholder || '',
    onChange: handleChange,
    options: options,
    value: selectedOptions,
    isMulti,
    variant: variant as Variant,
    size: size as SizeProp,
    isOptionDisabled: (option) => !!option.isDisabled,
    components: components,
    inputValue,
    onInputChange: handleInputChange,
    onFocus: handleFocus,
    onBlur: handleBlur,
    ...rest,
  };

  if (isCreatable) {
    return <CreatableSelect ref={ref} onCreateOption={onCreate} {...commonProps} />;
  }
  return <ReactSelect ref={ref} {...commonProps} />;
}

export const Select = forwardRefGeneric(SelectInner);
